import type { DocTypeSchema, DocFieldSchema } from "@/lib/erp/meta";

/**
 * Tab structure from meta fields
 */
export interface TabStructure {
  name: string;
  label: string;
  sections: SectionStructure[];
}

/**
 * Section structure within a tab
 */
export interface SectionStructure {
  name: string;
  label: string;
  columns: ColumnStructure[];
  
  // DocViewSchema v1: Widget ID for widget-based sections
  // If set, ShopifyStyleRenderer uses EXPERIENCE_WIDGETS registry
  _widgetId?: string;
  
  // DocViewSchema v1: Signal to collect unmapped meta fields
  _collectUnmapped?: boolean;
  
  // DocViewSchema v1: Section description (optional)
  description?: string;
}

/**
 * Column structure within a section
 */
export interface ColumnStructure {
  name: string;
  label: string;
  fields: DocFieldSchema[];
}

/**
 * Build tab structure from DocType metadata
 * 
 * Follows ERPNext pattern:
 * - Tab Break → creates new tab
 * - Section Break → creates new section within current tab
 * - Column Break → creates new column within current section
 * - Regular fields → added to current column
 * 
 * @param schema - DocType schema from meta API
 * @returns Array of tab structures
 */
export function buildTabsFromMeta(schema: DocTypeSchema): TabStructure[] {
  const tabs: TabStructure[] = [];
  let currentTab: TabStructure | null = null;
  let currentSection: SectionStructure | null = null;
  let currentColumn: ColumnStructure | null = null;

  /**
   * Create a new tab
   */
  function createTab(field: DocFieldSchema | null): TabStructure {
    return {
      name: field?.fieldname || "__details",
      label: field?.label || "Details",
      sections: [],
    };
  }

  /**
   * Create a new section
   */
  function createSection(field: DocFieldSchema | null): SectionStructure {
    return {
      name: field?.fieldname || "__section",
      label: field?.label || "",
      columns: [],
    };
  }

  /**
   * Create a new column
   */
  function createColumn(field: DocFieldSchema | null): ColumnStructure {
    return {
      name: field?.fieldname || "__column",
      label: field?.label || "",
      fields: [],
    };
  }

  /**
   * Ensure we have a tab, section, and column
   */
  function ensureStructure() {
    if (!currentTab) {
      currentTab = createTab(null);
      tabs.push(currentTab);
    }
    if (!currentSection) {
      currentSection = createSection(null);
      currentTab.sections.push(currentSection);
    }
    if (!currentColumn) {
      currentColumn = createColumn(null);
      currentSection.columns.push(currentColumn);
    }
  }

  // Process all fields
  for (const field of schema.fields) {
    // Skip hidden fields
    if (field.hidden === 1) {
      continue;
    }

    switch (field.fieldtype) {
      case "Tab Break":
        // Create new tab
        currentTab = createTab(field);
        tabs.push(currentTab);
        currentSection = null;
        currentColumn = null;
        break;

      case "Section Break":
        // Ensure we have a tab
        if (!currentTab) {
          currentTab = createTab(null);
          tabs.push(currentTab);
        }
        // Create new section
        currentSection = createSection(field);
        currentTab.sections.push(currentSection);
        currentColumn = null;
        break;

      case "Column Break":
        // Ensure we have tab and section
        if (!currentTab) {
          currentTab = createTab(null);
          tabs.push(currentTab);
        }
        if (!currentSection) {
          currentSection = createSection(null);
          currentTab.sections.push(currentSection);
        }
        // Create new column
        currentColumn = createColumn(field);
        currentSection.columns.push(currentColumn);
        break;

      default:
        // Regular field - add to current column
        ensureStructure();
        currentColumn!.fields.push(field);
        break;
    }
  }

  // Remove empty sections
  for (const tab of tabs) {
    tab.sections = tab.sections.filter(
      (section) => section.columns.some((col) => col.fields.length > 0)
    );
  }

  // Remove empty tabs (shouldn't happen, but safety check)
  return tabs.filter((tab) => tab.sections.length > 0);
}

